import type { CSSProperties, ReactNode } from 'react';
import { Box } from '@mui/material';
import { LIBRARY_PREVIEW_CANVAS_BG, LIBRARY_PREVIEW_LAYOUT_WIDTH_PX } from './libraryPreviewConstants';
import type { SheetPlacement } from './previewSheetScale';

const MOTION = 'var(--duration-fast) ease-out';

const reduceMotion = {
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
} as const;

export interface LibraryPreviewExpandProps {
  testId?: string;
  /** Resting well height. 0 until the crop has been measured — the window then fills the aspect box. */
  restHeightPx: number;
  openHeightPx: number;
  rest: SheetPlacement;
  open: SheetPlacement;
  /** Hover controls, positioned against the window so they sit on the opened page. */
  overlay?: ReactNode;
  children: ReactNode;
}

function px(value: number): string {
  return `${value}px`;
}

/**
 * The library-card preview window. Rest and open placements are CSS variables so the card's
 * `:hover` / `:focus-within` rule can switch them; the transition lives here, not on the shared
 * static sheet, so the Add panel does not animate. Reduced motion drops the transition and the
 * state still changes.
 */
export function LibraryPreviewExpand({ testId, restHeightPx, openHeightPx, rest, open, overlay, children }: LibraryPreviewExpandProps) {
  const variables = {
    '--lp-h-rest': px(restHeightPx),
    '--lp-h-open': px(openHeightPx),
    '--lp-scale-rest': String(rest.scale),
    '--lp-scale-open': String(open.scale),
    '--lp-x-rest': px(rest.xPx),
    '--lp-y-rest': px(rest.yPx),
    '--lp-x-open': px(open.xPx),
    '--lp-y-open': px(open.yPx)
  } as CSSProperties;

  return (
    <Box
      data-preview-window=""
      data-expanded-target="true"
      style={variables}
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1,
        boxSizing: 'border-box',
        overflow: 'hidden',
        pointerEvents: 'none',
        backgroundColor: LIBRARY_PREVIEW_CANVAS_BG,
        height: restHeightPx > 0 ? 'var(--lp-h-rest)' : '100%',
        '--lp-scale': 'var(--lp-scale-rest)',
        '--lp-x': 'var(--lp-x-rest)',
        '--lp-y': 'var(--lp-y-rest)',
        transition: `height ${MOTION}`,
        ...reduceMotion
      }}
    >
      <Box
        data-testid={testId ? `${testId}:sheet` : undefined}
        data-preview-sheet=""
        data-anchor="top-left"
        data-layout-width={LIBRARY_PREVIEW_LAYOUT_WIDTH_PX}
        data-rest-scale={rest.scale}
        data-open-scale={open.scale}
        data-open-y={open.yPx}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: LIBRARY_PREVIEW_LAYOUT_WIDTH_PX,
          pointerEvents: 'none',
          transform: 'translate(var(--lp-x), var(--lp-y)) scale(var(--lp-scale))',
          transformOrigin: 'top left',
          transition: `transform ${MOTION}`,
          ...reduceMotion
        }}
      >
        {children}
      </Box>
      {overlay}
    </Box>
  );
}
